import { Box, Typography, RadioGroup, Icon } from '@mui/material'
import { Field } from 'formik'
import { format, parseISO } from 'date-fns'

import { useGetEmployeeEntitlement } from 'services/api/modules/leave/entitlement'

import AdxRadioButton from 'components/custom/AdxRadioButton'
import AdxFormControlLabel from 'components/custom/AdxFormControlLabel'
import AdxStatusBar from 'components/reusable/statusBar/AdxStatusBar'
import AdxButton from 'components/custom/AdxButton'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'

const SelectLeaveView = ({ setPageState, setSelected, formikProps }) => {
  const { values, setFieldValue } = formikProps
  const { data: leaveBalance, isLoading } = useGetEmployeeEntitlement()

  if (isLoading) {
    return <Box>loading...</Box>
  }

  if (!leaveBalance) {
    return (
      <AdxEmptyStateV2
        icon={<Icon>priority_high</Icon>}
        title="No Leave Policies Found"
        subtitle="Please contact your HR for more information."
      />
    )
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column-reverse', lg: 'row' },
          alignItems: { xs: 'flex-start', lg: 'center' },
          justifyContent: 'space-between',
          px: 3,
          pt: 2,
          pb: 1,
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
          Select a leave type
        </Typography>
        {leaveBalance?.leave_credit[0]?.leave_credit?.granted ? (
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
            <Typography variant="body1">Available replacement credit:</Typography>

            {leaveBalance?.leave_credit[0]?.leave_credit_expiry && (
              <AdxStatusBar
                status={{
                  text: leaveBalance?.leave_credit[0]?.leave_credit_expiry[0]?.expiry_date
                    ? `${leaveBalance?.leave_credit[0]?.leave_credit_expiry[0]?.granted
                    } expiring on ${format(
                      parseISO(
                        leaveBalance?.leave_credit[0]?.leave_credit_expiry[0]?.expiry_date
                      ),
                      'dd MMM'
                    )}`
                    : 'No expiry',
                  color: 'yellow-02.main',
                  bgColor: 'light-yellow-01.main',
                }}
              />
            )}
            <Typography variant="body1" color="alternate.main">
              {leaveBalance.leave_credit[0].leave_credit.balance}
            </Typography>
          </Box>
        ) : null}
      </Box>
      <Box px={3} py={1} width="100%" flex={1}>
        <RadioGroup
          sx={{ width: '100%' }}
          name="selectedLeave"
          value={values.selectedLeave}
          onChange={(event) => {
            setFieldValue('selectedLeave', event.currentTarget.value)
            setSelected(JSON.parse(event.target.value))
          }}
        >
          {/* Content for with entitlement leave type */}
          <Typography variant="body2" color="light-gray-text.main">
            With Entitlement
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: 1,
              my: 1,
              width: '100%',
            }}
          >
            {leaveBalance.with_entitlement.map((item) => (
              <AdxFormControlLabel
                key={item.id}
                type="fill"
                selected={values.selectedLeave}
                value={JSON.stringify(item)}
                control={<Field as={AdxRadioButton} />}
                label={<RadioLabel item={item} type="WITH" leaveBalance={leaveBalance} />}
              />
            ))}
          </Box>
          {/* Content for without entitlement leave type */}
          {leaveBalance.without_entitlement.length > 0 && (
            <>
              <Typography variant="body2" color="light-gray-text.main">
                Without Entitlement
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: 1,
                  my: 1,
                  width: '100%',
                }}
              >
                {leaveBalance.without_entitlement.map((item) => (
                  <AdxFormControlLabel
                    key={item.id}
                    type="fill"
                    selected={values.selectedLeave}
                    value={JSON.stringify(item)}
                    control={<AdxRadioButton />}
                    label={<RadioLabel item={item} leaveBalance={leaveBalance} />}
                  />
                ))}
              </Box>{' '}
            </>
          )}
        </RadioGroup>
      </Box>
      <Box
        sx={{
          position: 'sticky',
          bottom: '0',
          width: '100%',
          display: 'flex',
          justifyContent: { xs: 'center', md: 'end' },
          alignItems: 'center',
          bgcolor: 'primary.light',
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'grayscale.line-light',
        }}
      >
        <AdxButton
          onClick={() => setPageState('FILL_DETAILS')}
          sx={{ width: { xs: '100%', md: '300px' } }}
          disabled={!values.selectedLeave || values.selectedLeave === '{}'}
        >
          Next
        </AdxButton>
      </Box>
    </Box>
  )
}

export default SelectLeaveView

// RADIOLABEL COMPONENT
const RadioLabel = ({ item, type, leaveBalance }) => {
  const usesAnotherPolicyBalance =
    item?.balance_source_leave_policy_id &&
    item?.balance_source_leave_policy_id !== item?.leave_policy_id

  const grandEntitlementAmount =
    item?.entitlement?.amount + (item?.carry_forward ? item?.carry_forward?.amount : 0)

  const grandEntitlementUsed = item?.entitlement?.utilised + item?.entitlement?.outstanding
  const displayBalance = grandEntitlementAmount - grandEntitlementUsed

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Typography sx={{ width: '50%' }} variant="subtitle1" color="dark-text.main">
        {item.leave_policy.name}
      </Typography>
      {type === 'WITH' && (
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
          {leaveBalance?.leave_credit[0]?.applicable_leave?.find(
            (uItem) => uItem.leave_policy_id === item.leave_policy_id
          ) && (
              <AdxStatusBar
                sx={{ py: 0.5, borderRadius: 1 }}
                status={{
                  text: 'Replacement credit utilizable',
                  color: 'white.main',
                  bgColor: 'alternate.main',
                }}
              />
            )}

          {usesAnotherPolicyBalance ? (
            <AdxStatusBar
              sx={{ py: 0.5, px: 1, borderRadius: 1 }}
              status={{
                text: `Deducts from ${item.balance_source_leave_policy_name}`,
                color: 'alternate.main',
                bgColor: 'primary.light',
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 0.5 }}>
              <Typography variany="body2" color="alternate.main">
                {displayBalance}
              </Typography>
              <Typography
                variany="body2"
                color="light-gray-text.main"
              >{` /${grandEntitlementAmount}`}</Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
